import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Bell, Lock, ShieldCheck, Mail, LogOut, Globe, BellRing, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import i18n, { SUPPORTED_LANGUAGES } from "../i18n";
import { pushSupported, enablePush, disablePush } from "../lib/push";

export default function Settings() {
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [emailReminders, setEmailReminders] = useState(true);
  const [reminderTime, setReminderTime] = useState("08:00");
  const [language, setLanguage] = useState(i18n.language);
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);

  useEffect(() => {
    if (pushSupported() && Notification.permission === "granted") {
      navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then((s) => setPushOn(!!s));
    }
  }, []);

  const togglePush = async (val) => {
    try {
      if (val) { await enablePush(); setPushOn(true); toast.success(t("settings.pushEnabled")); }
      else { await disablePush(); setPushOn(false); }
    } catch (e) {
      if (e.message === "unsupported") toast.error(t("settings.pushUnsupported"));
      else if (e.message === "denied") toast.error(t("settings.pushDenied"));
      else toast.error(t("settings.pushDenied"));
      setPushOn(false);
    }
  };

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setName(data.name || "");
      setEmailReminders(data.settings?.email_reminders ?? true);
      setReminderTime(data.settings?.reminder_time || "08:00");
      if (data.settings?.language) setLanguage(data.settings.language);
    });
  }, []);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    api.patch("/auth/settings", { language: lang }).catch(() => {});
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/settings", {
        name, email_reminders: emailReminders, reminder_time: reminderTime, language,
      });
      setUser({ ...user, name: data.name });
      toast.success(t("settings.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) toast.success(t("settings.pushTestSent"));
      else toast.error(t("settings.pushTestNone"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("settings.pushTestNone"));
    }
  };

  return (
    <Layout>
      <div className="animate-fade-up" data-testid="settings-page">
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight">{t("settings.title")}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{t("settings.subtitle")}</p>
      </div>

      <div className="mt-8 space-y-6 max-w-lg">
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-semibold font-display flex items-center gap-2"><Mail className="h-4 w-4 brand-text" /> {t("settings.account")}</h2>
          <div className="mt-4 space-y-4">
            <div><Label className="text-xs">{t("auth.name")}</Label><Input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div><Label className="text-xs">{t("settings.emailReadonly")}</Label><Input value={user?.email || ""} disabled /></div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-semibold font-display flex items-center gap-2"><Globe className="h-4 w-4 brand-text" /> {t("settings.language")}</h2>
          <p className="text-xs text-muted-foreground mt-1">{t("settings.languageDesc")}</p>
          <div className="mt-4">
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger data-testid="settings-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUPPORTED_LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
                    <span className="mr-2">{l.flag}</span>{l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-semibold font-display flex items-center gap-2"><Bell className="h-4 w-4 brand-text" /> {t("settings.remindersSection")}</h2>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("settings.emailReminders")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.emailRemindersDesc")}</p>
              </div>
              <Switch data-testid="settings-email-reminders" checked={emailReminders} onCheckedChange={setEmailReminders} />
            </div>
            <div>
              <Label className="text-xs">{t("settings.preferredTime")}</Label>
              <Select value={reminderTime} onValueChange={setReminderTime}>
                <SelectTrigger data-testid="settings-reminder-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["06:00", "08:00", "09:00", "12:00", "18:00", "20:00"].map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground bg-secondary rounded-lg p-2">
              {t("settings.emailNote")}
            </p>
            <div className="flex items-center justify-between border-t border-border pt-4">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5"><BellRing className="h-3.5 w-3.5 brand-text" /> {t("settings.pushTitle")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.pushDesc")}</p>
              </div>
              <Switch data-testid="settings-push" checked={pushOn} onCheckedChange={togglePush} />
            </div>
            {pushOn && (
              <Button data-testid="settings-push-test" variant="outline" size="sm" onClick={sendTest}>
                <BellRing className="h-3.5 w-3.5 mr-1" /> {t("settings.pushTest")}
              </Button>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6" data-testid="settings-premium-card">
          <h2 className="font-semibold font-display flex items-center gap-2"><Crown className="h-4 w-4 brand-text" /> {t("billing.sectionTitle")}</h2>
          {user?.premium ? (
            <div className="mt-3">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold brand-text bg-accent/60 rounded-full px-3 py-1" data-testid="settings-premium-active">
                <Crown className="h-3.5 w-3.5" /> {t("billing.activeBadge")}
              </span>
              <p className="text-xs text-muted-foreground mt-2">{t("billing.activeDesc")}</p>
            </div>
          ) : (
            <div className="mt-3">
              <p className="text-sm text-muted-foreground">{t("billing.upsell")}</p>
              <Button data-testid="settings-upgrade-button" onClick={() => navigate("/pricing")} className="mt-3 brand-bg text-white hover:opacity-90">
                <Crown className="h-4 w-4 mr-1.5" /> {t("billing.upgradeCta")}
              </Button>
            </div>
          )}
        </div>

        <div className="bg-accent/40 border border-border rounded-xl p-6">
          <h2 className="font-semibold font-display flex items-center gap-2"><ShieldCheck className="h-4 w-4 brand-text" /> {t("settings.privacy")}</h2>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 brand-text" /> {t("settings.privacy1")}</li>
            <li className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 brand-text" /> {t("settings.privacy2")}</li>
            <li className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 brand-text" /> {t("settings.privacy3")}</li>
          </ul>
        </div>

        <div className="flex items-center justify-between">
          <Button data-testid="settings-save" onClick={save} disabled={saving} className="brand-bg text-white hover:opacity-90">
            {saving ? t("common.saving") : t("common.saveChanges")}
          </Button>
          <Button data-testid="settings-logout" variant="outline" onClick={doLogout} className="text-red-600">
            <LogOut className="h-4 w-4 mr-1" /> {t("common.logout")}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
